/**
 * Shared TanStack Query client factory with global error logging.
 *
 * Provides consistent defaults (staleTime 5min, retry 1) and
 * centralised error logging via QueryCache/MutationCache callbacks.
 * Does NOT show toasts — components that already render errors inline
 * remain in control. This is the future Sentry integration point.
 *
 * @module utils/queryClient
 */
import { QueryClient, QueryCache, MutationCache } from "@tanstack/react-query";
import { getUserFriendlyErrorMessage } from "./apiError";

export function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        const message = getUserFriendlyErrorMessage(error, String(query.queryKey));
        console.error(`[QueryClient] query ${String(query.queryKey)} failed:`, message);
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        const key = mutation.options.mutationKey
          ? String(mutation.options.mutationKey)
          : "unknown";
        const message = getUserFriendlyErrorMessage(error, key);
        console.error(`[QueryClient] mutation ${key} failed:`, message);
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000, // 5 minutes default (stable data)
        retry: 1,
      },
    },
  });
}
